import * as React from 'react'
// libs
import { useSelector } from 'react-redux'
import { useMutation, useQuery } from '@apollo/client/react'
// graphql
import { DEFAULT_QUERY } from 'graphql/default'
import { RESOURCE_DELETE_MUTATION } from 'graphql/resource/mutations'
// store
import { objectEntitiesSelector } from 'store/reducers/objectEntities'
// constants
import { OBJECT_ENTITY_NAMES } from 'constants/objects'
// types
import { NGetActivities } from 'types/activities/helpers'
import { TRefetch } from 'types/refetch'
import {
  ILog,
  IPlan,
  IField,
  IPlanKpi,
  IPlanAction,
  LogEntityTypes,
  IPlanObjective,
  ILogFilterInput,
  IPlanObjectiveResource
} from 'types/graphql'
// utils
import { getFieldsList } from 'utils/fields'
import { map, orderBy, pipe } from 'lodash/fp'
import { parseGraphQLErrorMessages, safeGetOr, sortActivitiesByDate } from 'utils/index'

export declare namespace NResourcesListAPI {
  export interface IContext {
    entity: ILog & IPlan & IPlanAction & IPlanKpi & IPlanObjective
    fields: IField[]
    loading: boolean
    refetch: TRefetch<NGetActivities.IResponse, NGetActivities.IVariables>
    resources: any
    canCreate: boolean
    canDelete: boolean
    canModify: boolean
    deleteResource: (resource: IPlanObjectiveResource) => void
  }

  interface IIncomingProps {
    skip?: boolean
    query: any
    entity: ILog & IPlan & IPlanAction & IPlanKpi & IPlanObjective
    children?: React.ReactNode
    variables: ILogFilterInput
    canCreate?: boolean
    canDelete?: boolean
    canModify?: boolean
  }
}

export const ResourcesListAPIContext = React.createContext<NResourcesListAPI.IContext>({} as any)

const ResourcesListAPI: React.FunctionComponent<NResourcesListAPI.IIncomingProps> = ({
  skip,
  query,
  entity,
  children,
  variables,
  canCreate,
  canDelete,
  canModify
}) => {
  const { data, loading, refetch } = useQuery(query || DEFAULT_QUERY, {
    skip,
    variables,
    fetchPolicy: 'network-only'
  })

  const objectEntities = useSelector(objectEntitiesSelector)

  const [deleteResourceMutation] = useMutation(RESOURCE_DELETE_MUTATION)

  const fields = getFieldsList(objectEntities, OBJECT_ENTITY_NAMES.RESOURCE)

  const resources = pipe(
    safeGetOr<any, any, any>([], 'planResources.data'),
    map((resource: IPlanObjectiveResource) => ({ ...resource, entityType: LogEntityTypes.Resource })),
    orderBy(['createdAt'], ['desc']),
    sortActivitiesByDate
  )(data)

  const deleteResource = async (resource) => {
    try {
      await deleteResourceMutation({
        variables: {
          id: resource.id
        }
      })

      refetch()
    } catch (errors) {
      console.log('[ResourcesListAPI.deleteResource]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const value = {
    entity,
    fields,
    loading,
    refetch,
    canCreate,
    canDelete,
    canModify,
    deleteResource,
    resources: resources || []
  } as any

  return <ResourcesListAPIContext.Provider value={value}>{children}</ResourcesListAPIContext.Provider>
}

export default ResourcesListAPI
